import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import { LeadAddressFields, LEAD_ADDR_FIELDS } from './LeadAddressFields';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE = [['client_name', 'Nome'], ['client_phone', 'Telefone'], ['client_email', 'Email'], ['client_nif', 'NIF']];
const ALL_KEYS = [...BASE.map(([k]) => k), ...LEAD_ADDR_FIELDS.map(([k]) => k)];

/** Completar / editar dados da lead. Comercial e Chefe de Equipa só preenchem campos vazios. */
export const LeadDataDialog = ({ leadId, open, onClose, onSaved, onlyFields, title }) => {
  const { user } = useAuth();
  const restricted = ['comercial', 'chefe_equipa'].includes(user?.role);
  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/leads/${leadId}`).then(({ data }) => {
      const l = data.lead || data;
      const o = Object.fromEntries(ALL_KEYS.map((k) => [k, l[k] || '']));
      setOrig(o); setForm(o);
    }).catch(() => toast.error('Erro ao carregar a lead'));
  }, [open, leadId]);

  const isLocked = (k) => restricted && !!(orig?.[k] || '').trim();
  const show = (k) => !onlyFields || onlyFields.includes(k);
  const save = async () => {
    const changed = Object.fromEntries(ALL_KEYS.filter((k) => (form[k] || '') !== (orig[k] || '')).map((k) => [k, form[k]]));
    const missingReq = (onlyFields || []).filter((k) => !(form[k] || '').trim());
    if (missingReq.length) { toast.error('Preencha todos os campos em falta'); return; }
    if (!Object.keys(changed).length) { onClose(); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/leads/${leadId}`, changed);
      toast.success('Dados da lead guardados');
      onSaved?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-data-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{title || 'Completar / Editar dados'}</DialogTitle></DialogHeader>
        {restricted && !onlyFields && <p className="text-xs text-slate-500 -mt-2 flex items-center gap-1"><Lock className="w-3 h-3" />Os campos já preenchidos só podem ser alterados pelo Gestor.</p>}
        {!orig ? <p className="text-sm text-slate-400">A carregar…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {BASE.filter(([k]) => show(k)).map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs flex items-center gap-1">{isLocked(k) && <Lock className="w-3 h-3 text-slate-400" />}{l}{onlyFields ? ' *' : ''}</Label>
                  <Input value={form[k] || ''} disabled={isLocked(k)} maxLength={k === 'client_nif' ? 9 : 200}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`lead-data-${k}`} />
                </div>
              ))}
            </div>
            {!onlyFields && (
              <>
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Morada</p>
                <LeadAddressFields value={form} onChange={(p) => setForm((f) => ({ ...f, ...p }))} isLocked={isLocked} testPrefix="lead-data" />
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={saving || !orig} onClick={save} className="bg-[#2D3E6F] hover:bg-[#24335c]" data-testid="lead-data-save">{onlyFields ? 'Guardar e continuar' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
